import dayjs from 'dayjs';

import {
  formatDateTimeArray,
  formatInstantInZone,
  isDateTimeArray,
  resolveMarketplaceTimeZone,
} from '@/helpers/dateHelpers.js';
import { transformText } from '@/helpers/helpers.js';
import { formatMoney } from '@/helpers/moneyFormatter.js';
import { useAccountStore } from '@/stores/account.js';

const checkInfinity = (value) => {
  if (value === 'Infinity' || value === Infinity) return '∞';
  if (value === '-Infinity' || value === -Infinity) return '-∞';
  return null;
};

const checkSpecialValues = (value, options = { nanAsDefault: true }) => {
  // Сначала проверяем на Infinity
  const infinityCheck = checkInfinity(value);
  if (infinityCheck !== null) {
    return infinityCheck;
  }

  // Затем проверяем на NaN (используем Number.isNaN для точной проверки)
  // isNaN() даёт ложные срабатывания для null, undefined, strings
  if (value === 'NaN' || Number.isNaN(value) || (typeof value === 'number' && isNaN(value))) {
    return options.nanAsDefault ? '—' : 'NaN';
  }

  // Если это обычное значение, возвращаем null
  return null;
};

export const formatValue = (
  value,
  typeFormat,
  locale = navigator.language,
  currencyCode = null,
) => {
  // Проверяем специальные значения для всех типов форматирования
  const specialValue = checkSpecialValues(value, {
    nanAsDefault: true,
  });
  if (specialValue !== null) {
    return specialValue;
  }

  const getNarrowCurrencySymbol = (currencyCode, locale = navigator.language) => {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    // Format a known number and strip the number itself to isolate the symbol
    const formatted = formatter.format(1);

    // Use regex to extract non-digit parts (symbol)
    const symbol = formatted.replace(/[\d\s.,]/g, '').trim();

    return symbol;
  };

  const account = useAccountStore();

  const formatters = {
    currency: function (value) {
      const currency = currencyCode || account.currentMarketplace?.currencyCode;
      if (currency) {
        return value == null
          ? null
          : formatMoney(Number(value), currency, {
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
      } else {
        return value == null
          ? null
          : '¤' +
              formatMoney(value, null, {
                currencyDisplay: 'amount',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
      }
    },
    percent: function (value) {
      if (value == null || Number.isNaN(value)) return '—';
      const num = Number(value);
      if (!Number.isFinite(num)) return '∞';
      return `${num.toFixed(2)}%`;
    },
    percentInteger: function (value) {
      if (value == null || Number.isNaN(value)) return '—';
      const num = Number(value);
      if (!Number.isFinite(num)) return '∞';
      return `${Math.round(num).toLocaleString(locale)}%`;
    },
    formatNumber: function (value) {
      if (value == null) return null;
      // convert ot fixed if value has decimals
      if (Number(value) % 1 !== 0) {
        return Number(value).toFixed(2).toLocaleString(locale);
      }
      return Number(value).toLocaleString(locale);
    },
    formatNumberToFixed: function (value) {
      return value == null ? null : Number(value).toFixed(2).toLocaleString(locale);
    },
    formatCurrencyNumber: function (value) {
      return value == null ? null : this.currency(value);
    },
    date: function (value) {
      if (value === '1970-01-01T00:00:00') return '—';
      // Обработка массива даты
      if (Array.isArray(value)) {
        const [year, month, day, hour, minute, second, millisecond] = value;
        // Создаем дату из массива (месяц в JS начинается с 0)
        const date = dayjs(
          new Date(year, month - 1, day, hour, minute, second, millisecond / 1000000),
        );
        return date.format('MMM D, YYYY');
      }

      return value == null ? null : dayjs(value).format('MMM D, YYYY');
    },
    dateTime: function (value) {
      if (value == null) return null;

      // Обработка массива даты
      if (Array.isArray(value)) {
        if (value.length === 7) {
          // Полная дата с миллисекундами [year, month, day, hour, minute, second, millisecond]
          const [year, month, day, hour, minute, second, millisecond] = value;
          const date = dayjs(
            new Date(year, month - 1, day, hour, minute, second, millisecond / 1000000),
          );
          return date.format('MMM D, YYYY, HH:mm');
        } else if (value.length === 5) {
          // Дата с часами и минутами [year, month, day, hour, minute]
          const [year, month, day, hour, minute] = value;
          const date = dayjs(new Date(year, month - 1, day, hour, minute));
          return date.format('MMM D, YYYY, HH:mm');
        }
      }

      // A real instant (naive strings are UTC): shown in the current marketplace's zone with a
      // short zone suffix, never the browser's. Unparseable input renders as null.
      const timeZone = resolveMarketplaceTimeZone({
        marketplace: account.currentMarketplace,
        businessProfile: account.currentBusinessProfile,
      });
      return formatInstantInZone(value, timeZone, {
        locale: 'en-US',
        hourCycle: 'h23',
        hour: '2-digit',
        fallback: null,
      });
    },
    datetimeArray: function (value) {
      if (value == null) return null;
      if (isDateTimeArray(value)) {
        return formatDateTimeArray(value);
      }
      return dayjs.utc(value).local().format('MMM D, YYYY, HH:mm');
    },
    defaultText: function (value) {
      return value;
    },
    text: function (value) {
      if (value === null) return null;
      return transformText(value);
    },
    multiplier: function (value) {
      if (value == null || Number.isNaN(value)) return '—';
      const num = Number(value);
      if (!Number.isFinite(num)) return '∞';
      return `${num.toFixed(2)}x`;
    },
    modify: function (value) {
      if (value == null) return null;
      const absValue = Math.abs(value);
      if (absValue >= 1e9) {
        return (value / 1e9).toFixed(1) + 'B';
      } else if (absValue >= 1e6) {
        return (value / 1e6).toFixed(1) + 'M';
      } else if (absValue >= 1e3) {
        return (value / 1e3).toFixed(1) + 'K';
      } else {
        return Number(value).toLocaleString(locale);
      }
    },
    currencyModify: function (value) {
      if (value == null) return null;

      const currency = currencyCode || account.currentMarketplace?.currencyCode;
      const currencySymbol = currency ? getNarrowCurrencySymbol(currency, locale) : '¤';

      const absValue = Math.abs(value);
      if (absValue >= 1e9) {
        return `${currencySymbol}${(value / 1e9).toFixed(1)}B`; // Billion
      } else if (absValue >= 1e6) {
        return `${currencySymbol}${(value / 1e6).toFixed(1)}M`; // Million
      } else if (absValue >= 1e3) {
        return `${currencySymbol}${(value / 1e3).toFixed(1)}K`; // Thousand
      } else {
        return this.currency(value);
      }
    },
  };

  if (typeFormat && formatters[typeFormat]) {
    return formatters[typeFormat](value);
  }

  return value;
};

export const tickFormatCallbacks = () => {
  const account = useAccountStore();
  return {
    currency: function (value) {
      // Проверяем специальные значения
      const specialValue = checkSpecialValues(value);
      if (specialValue !== null) {
        return specialValue;
      }

      const currency = account.currentMarketplace?.currencyCode || 'USD';
      return Number(value).toLocaleString('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    percent: function (value) {
      // Проверяем специальные значения с особой обработкой для NaN
      const specialValue = checkSpecialValues(value, { nanAsDefault: false });
      if (specialValue !== null) {
        return specialValue;
      }

      if (value || value === 0) {
        return `${value < 0 ? '—' : ''}${Math.abs(value).toFixed(2)}%`;
      }
    },
    formatNumber: function (value) {
      // Проверяем специальные значения
      const specialValue = checkSpecialValues(value);
      if (specialValue !== null) {
        return specialValue;
      }

      return Math.trunc(Number(value)).toLocaleString('en-US');
    },
    formatNumberToFixed: function (value) {
      // Проверяем специальные значения
      const specialValue = checkSpecialValues(value);
      if (specialValue !== null) {
        return specialValue;
      }

      return value == null ? null : Number(value.toFixed(2)).toLocaleString('en-US');
    },
    modify: function (value) {
      // Проверяем специальные значения
      const specialValue = checkSpecialValues(value);
      if (specialValue !== null) {
        return specialValue;
      }

      const sign = value < 0 ? '—' : '';
      const absValue = Math.abs(value);

      if (absValue >= 1e9) {
        return sign + (absValue / 1e9).toFixed(1) + 'B'; // Billion
      } else if (absValue >= 1e6) {
        return sign + (absValue / 1e6).toFixed(1) + 'M'; // Million
      } else if (absValue >= 1e3) {
        return sign + (absValue / 1e3).toFixed(1) + 'K'; // Thousand
      } else {
        return sign + absValue.toLocaleString('en-US');
      }
    },
    formatCurrencyNumber: function (value) {
      // Проверяем специальные значения
      const specialValue = checkSpecialValues(value);
      if (specialValue !== null) {
        return specialValue;
      }

      return value === null || value === undefined ? null : Number(value).toFixed(2);
    },
    currencyModify: function (value) {
      // Проверяем специальные значения
      const specialValue = checkSpecialValues(value);
      if (specialValue !== null) {
        return specialValue;
      }

      const currency = account.currentMarketplace?.currencyCode || 'USD'; // Динамическая валюта
      const sign = value < 0 ? '-' : '';
      const absValue = Math.abs(value);

      const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency });

      if (absValue >= 1e9) {
        return (
          sign +
          formatter.format(absValue / 1e9).replace(formatter.format(1).replace(/1/, ''), '') +
          'B'
        ); // Billion
      } else if (absValue >= 1e6) {
        return (
          sign +
          formatter.format(absValue / 1e6).replace(formatter.format(1).replace(/1/, ''), '') +
          'M'
        ); // Million
      } else if (absValue >= 1e3) {
        return (
          sign +
          formatter.format(absValue / 1e3).replace(formatter.format(1).replace(/1/, ''), '') +
          'K'
        ); // Thousand
      } else {
        return sign + formatter.format(absValue); // Обычный формат
      }
    },
    defaultFormat: function (value) {
      // Проверяем специальные значения
      const specialValue = checkSpecialValues(value);
      if (specialValue !== null) {
        return specialValue;
      }

      return value;
    },
  };
};
